import React from "react"
import { useTranslation } from "react-i18next"
import { ReportedHoursGroupBy } from "../reported-hours/types"

type Translations = Omit<typeof defaultTranslations, "audit"> & {
  audit: Omit<typeof defaultTranslations.audit, "by"> & {
    by: (values: { action: string; person: string; date: string }) => string
  }
}

const defaultTranslations = {
  noDataText: "No time entries found for this date range.",
  notSelected: "Not selected",
  actions: {
    add: "Add time entry",
    edit: "Edit time entry",
    delete: "Delete time entry",
  },
  audit: {
    reported: "Logged",
    checked: "Reviewed",
    approved: "Approved",
    readyForCheck: "Marked as ready for review",
    by: "{{action}} by {{person}}<br/>{{action}} at {{date}}",
  },
  statuses: {
    reported: "Logged",
    readyForCheck: "Ready for review",
    readyForApproval: "Ready for approval",
    approved: "Approved",
  },
  labels: {
    date: "Date",
    projectTask: "Project/Task",
    customer: "Customer",
    description: "Description",
    started: "Started",
    finished: "Finished",
    hours: "Time",
    status: "Status",
    worker: "Member",
    readyForCheck: "Ready for review",
    readyForApproval: "Ready for approval",
    approved: "Approved",
    totalHours: "Total time",
    totalReportedHours: "Total time",
    headerRow: "Header",
    titleRow: "Title row",
    totalHoursRow: "Total time row",
    groupBy: {
      [ReportedHoursGroupBy.NO_GROUPING]: "No grouping",
      [ReportedHoursGroupBy.WORKER]: "Group by member",
      [ReportedHoursGroupBy.PROJECT]: "Group by project",
      [ReportedHoursGroupBy.CUSTOMER]: "Group by customer",
      [ReportedHoursGroupBy.STATUS]: "Group by status",
    },
  },
  dialogs: {
    close: "Close",
    cancel: "Cancel",
    date: "Date",
    projectTask: "Project / Task",
    hours: "Time",
    reportedBy: "Logged by",
    summaryAriaLabel: "Time entries summary",
    reminderSent: "Reminder has been sent.",
    reminderError: "Reminder could not be sent.",
    sendReminder: "Send reminder",
    retrySending: "Try sending again",
    days: "Days",
    reports: "Time entries",
  },
}

export const useHourReportTranslations = (): Translations => {
  const { t: typedT } = useTranslation("hour-report")
  const t = typedT as unknown as (key: string, options: Record<string, unknown>) => string

  return React.useMemo(
    () => ({
      noDataText: t("grid.noReports", { defaultValue: defaultTranslations.noDataText }),
      notSelected: t("list.notSelected", { defaultValue: defaultTranslations.notSelected }),
      actions: {
        add: t("actions.add", { defaultValue: defaultTranslations.actions.add }),
        edit: t("actions.editReport", { defaultValue: defaultTranslations.actions.edit }),
        delete: t("actions.deleteReport", { defaultValue: defaultTranslations.actions.delete }),
      },
      audit: {
        reported: t("gridAudit.reported", { defaultValue: defaultTranslations.audit.reported }),
        checked: t("gridAudit.checked", { defaultValue: defaultTranslations.audit.checked }),
        approved: t("gridAudit.approved", { defaultValue: defaultTranslations.audit.approved }),
        readyForCheck: t("gridAudit.readyForCheck", { defaultValue: defaultTranslations.audit.readyForCheck }),
        by: ({ action, person, date }) =>
          t("gridAudit.by", {
            defaultValue: defaultTranslations.audit.by,
            action,
            person,
            date,
            interpolation: { escapeValue: false },
          }),
      },
      statuses: {
        reported: t("status.reported", { defaultValue: defaultTranslations.statuses.reported }),
        readyForCheck: t("status.readyForCheck", { defaultValue: defaultTranslations.statuses.readyForCheck }),
        readyForApproval: t("status.readyForApproval", {
          defaultValue: defaultTranslations.statuses.readyForApproval,
        }),
        approved: t("status.approved", { defaultValue: defaultTranslations.statuses.approved }),
      },
      labels: {
        date: t("grid.date", { defaultValue: defaultTranslations.labels.date }),
        projectTask: t("grid.projectTask", { defaultValue: defaultTranslations.labels.projectTask }),
        customer: t("grid.customer", { defaultValue: defaultTranslations.labels.customer }),
        description: t("grid.description", { defaultValue: defaultTranslations.labels.description }),
        started: t("grid.started", { defaultValue: defaultTranslations.labels.started }),
        finished: t("grid.finished", { defaultValue: defaultTranslations.labels.finished }),
        hours: t("grid.hours", { defaultValue: defaultTranslations.labels.hours }),
        status: t("grid.status", { defaultValue: defaultTranslations.labels.status }),
        worker: t("grid.worker", { defaultValue: defaultTranslations.labels.worker }),
        readyForCheck: t("grid.readyForCheck", { defaultValue: defaultTranslations.labels.readyForCheck }),
        readyForApproval: t("grid.readyForApproval", {
          defaultValue: defaultTranslations.labels.readyForApproval,
        }),
        approved: t("grid.approved", { defaultValue: defaultTranslations.labels.approved }),
        totalHours: t("grid.totalHours", { defaultValue: defaultTranslations.labels.totalHours }),
        totalReportedHours: t("grid.totalReportedHours", {
          defaultValue: defaultTranslations.labels.totalReportedHours,
        }),
        headerRow: t("grid.headerRow", { defaultValue: defaultTranslations.labels.headerRow }),
        titleRow: t("grid.titleRow", { defaultValue: defaultTranslations.labels.titleRow }),
        totalHoursRow: t("grid.totalHoursRow", { defaultValue: defaultTranslations.labels.totalHoursRow }),
        groupBy: {
          [ReportedHoursGroupBy.NO_GROUPING]: t("grid.noGrouping", {
            defaultValue: defaultTranslations.labels.groupBy[ReportedHoursGroupBy.NO_GROUPING],
          }),
          [ReportedHoursGroupBy.WORKER]: t("grid.groupByWorker", {
            defaultValue: defaultTranslations.labels.groupBy[ReportedHoursGroupBy.WORKER],
          }),
          [ReportedHoursGroupBy.PROJECT]: t("grid.groupByProject", {
            defaultValue: defaultTranslations.labels.groupBy[ReportedHoursGroupBy.PROJECT],
          }),
          [ReportedHoursGroupBy.CUSTOMER]: t("grid.groupByCustomer", {
            defaultValue: defaultTranslations.labels.groupBy[ReportedHoursGroupBy.CUSTOMER],
          }),
          [ReportedHoursGroupBy.STATUS]: t("grid.groupByStatus", {
            defaultValue: defaultTranslations.labels.groupBy[ReportedHoursGroupBy.STATUS],
          }),
        },
      },
      dialogs: {
        close: t("actions.close", { defaultValue: defaultTranslations.dialogs.close }),
        cancel: t("gridDialogs.cancel", { defaultValue: defaultTranslations.dialogs.cancel }),
        date: t("grid.date", { defaultValue: defaultTranslations.dialogs.date }),
        projectTask: t("grid.projectTask", { defaultValue: defaultTranslations.dialogs.projectTask }),
        hours: t("grid.hours", { defaultValue: defaultTranslations.dialogs.hours }),
        reportedBy: t("gridDialogs.reportedBy", { defaultValue: defaultTranslations.dialogs.reportedBy }),
        summaryAriaLabel: t("grid.summaryAriaLabel", {
          defaultValue: defaultTranslations.dialogs.summaryAriaLabel,
        }),
        reminderSent: t("gridDialogs.reminderSent", { defaultValue: defaultTranslations.dialogs.reminderSent }),
        reminderError: t("gridDialogs.reminderError", { defaultValue: defaultTranslations.dialogs.reminderError }),
        sendReminder: t("gridDialogs.sendReminder", { defaultValue: defaultTranslations.dialogs.sendReminder }),
        retrySending: t("gridDialogs.retrySending", { defaultValue: defaultTranslations.dialogs.retrySending }),
        days: t("gridDialogs.days", { defaultValue: defaultTranslations.dialogs.days }),
        reports: t("gridDialogs.reports", { defaultValue: defaultTranslations.dialogs.reports }),
      },
    }),
    [t]
  )
}
